import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, matchPath } from 'react-router-dom';
// material-ui
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

// project import
import NavItem from './NavItem';
import { useGetMenuMaster } from 'api/menu';
import { handlerDrawerOpen } from 'api/menu';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DownOutlined, RightOutlined } from '@ant-design/icons';

// ==============================|| NAVIGATION - COLLAPSE ITEM ||============================== //

function NavCollapse({ menuItem }) {
  const { pathname } = useLocation();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const hasActiveChild = menuItem.children?.some(
    (child) => child.url && !!matchPath({ path: child.url, end: false }, pathname)
  );
  const [open, setOpen] = useState(!!hasActiveChild);

  useEffect(() => {
    if (hasActiveChild && !open) setOpen(true);
  }, [hasActiveChild]);

  const Icon = menuItem.icon;
  const itemIcon = Icon ? (
    <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} />
  ) : null;

  const handleToggle = () => {
    setOpen((prev) => !prev);
    if (downLG) handlerDrawerOpen(false);
  };

  return (
    <>
      <ListItemButton
        onClick={handleToggle}
        sx={(theme) => {
          const hoverBg = theme.palette.action.hover;
          return {
            zIndex: 1201,
            pl: drawerOpen ? '28px' : 1.5,
            py: !drawerOpen ? 1.25 : 1,
            '&:hover': { bgcolor: hoverBg }
          };
        }}
      >
        {itemIcon && (
          <ListItemIcon sx={{ minWidth: 28, color: 'text.primary' }}>
            {itemIcon}
          </ListItemIcon>
        )}
        {drawerOpen && (
          <ListItemText
            primary={
              <Typography variant="h6" color="text.primary">
                {menuItem.title}
              </Typography>
            }
          />
        )}
        {drawerOpen && (open ? <DownOutlined style={{ fontSize: 12 }} /> : <RightOutlined style={{ fontSize: 12 }} />)}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ py: 0 }}>
          {menuItem.children?.map((child) =>
            child.type === 'item' ? (
              <NavItem key={child.id} item={child} level={2} />
            ) : null
          )}
        </List>
      </Collapse>
    </>
  );
}

NavCollapse.propTypes = { menuItem: PropTypes.object };

// ==============================|| NAVIGATION - LIST GROUP ||============================== //

export default function NavGroup({ item }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const navCollapse = item.children?.map((menuItem) => {
    switch (menuItem.type) {
      case 'collapse':
        return <NavCollapse key={menuItem.id} menuItem={menuItem} />;
      case 'item':
        return <NavItem key={menuItem.id} item={menuItem} level={1} />;
      default:
        return (
          <Typography key={menuItem.id} variant="h6" color="error" align="center">
            Fix - Group Collapse or Items
          </Typography>
        );
    }
  });

  return (
    <List
      subheader={
        item.title &&
        drawerOpen && (
          <Box sx={{ pl: 3, mb: 1.5 }}>
            <Typography variant="subtitle2" color="textSecondary">
              {item.title}
            </Typography>
            {/* only available in paid version */}
          </Box>
        )
      }
      sx={{ mb: drawerOpen ? 1.5 : 0, py: 0, zIndex: 0 }}
    >
      {navCollapse}
    </List>
  );
}

NavGroup.propTypes = { item: PropTypes.object };
